import axios from "axios";
import SvgGoogleLogo from "../assets/google_logo";
import { BACKEND_URL, GOOGLE_CLIENT_ID } from "../config";
import { TokenResponse, useGoogleLogin } from "@react-oauth/google";
import { Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { SpinningSvg } from "../components/spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  handleClickBack,
  logInWithAccessToken,
  passTokenToApp,
  setJwtTokenAndRedirect,
} from "@/auth_utils";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    username: "",
    password: "",
    non_field_errors: "",
  });

  useEffect(() => {
    const jwt_token = localStorage.getItem("auth_token");
    if (jwt_token) {
      passTokenToApp(jwt_token);
    }
  }, []);

  const onFormSubmit = async (event: any) => {
    event.preventDefault();
    setLoading(true);
    const formData = new FormData(event.target);
    const data = Object.fromEntries(formData.entries());
    const response = await axios
      .post(BACKEND_URL + "/api/auth/", data, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setJwtTokenAndRedirect(response.data.token);
      })
      .catch((error) => {
        setErrors(error.response.data);
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onGoogleSuccess = (
    tokenResponse: Omit<
      TokenResponse,
      "error" | "error_description" | "error_uri"
    >,
  ) => {
    setLoading(true);
    checkTokenInfo(tokenResponse);
  };

  const onError = (error: any) => {
    console.log("login failed:", error);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: onGoogleSuccess,
    onError,
  });

  const checkTokenInfo = async (tokenInfo: TokenResponse) => {
    if (tokenInfo) {
      axios
        .get(
          `https://www.googleapis.com/oauth2/v1/userinfo?alt=json&access_token=${tokenInfo["access_token"]}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenInfo["access_token"]}`,
            },
          },
        )
        .then(
          async (response) => {
            const newAuthToken = await logInWithAccessToken(
              tokenInfo["access_token"],
              response["data"],
            );
          },
          (error) => {
            console.log(error);
          },
        )
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-gray-100 px-4">
      {/* One tap login
        <div
        id="g_id_onload"
        data-client_id={GOOGLE_CLIENT_ID}
        data-login_uri={window.location.href}
      ></div> */}
      <div className="mx-auto max-w-md w-full space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight">
            Log in to your account
          </h1>
          <p className="mt-2 text-gray-500">
            Don't have an account?{" "}
            <Link
              className="font-bold text-primary hover:underline"
              to="/register"
            >
              Register
            </Link>
          </p>
        </div>
        <div className="space-y-4">
          <Button
            variant="outline"
            className="flex w-full"
            onClick={() => googleLogin()}
          >
            <div style={{ display: "flex" }}>
              <div
                style={{ height: 18, marginRight: 8, minWidth: 18, width: 18 }}
              >
                <SvgGoogleLogo width={20} height={20} />
              </div>
              <div
                className="font-normal"
                style={{
                  fontFamily: '"Google Sans", arial, sans-serif',
                }}
              >
                Log in with Google
              </div>
            </div>
          </Button>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-gray-100 px-2 text-gray-500">
                Or continue with
              </span>
            </div>
          </div>
          <form className="space-y-4" onSubmit={onFormSubmit}>
            <div>
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Email address
              </label>
              <Input
                id="username"
                className="bg-gray-50"
                name="username"
                placeholder="name@example.com"
                type="email"
              />
              {errors.username && (
                <p className="text-red-500 ">{errors.username}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Password
                </label>
                <a
                  className="text-sm font-medium text-gray-900 hover:underline"
                  href="#"
                >
                  Forgot password?
                </a>
              </div>
              <Input
                id="password"
                className="bg-gray-50"
                name="password"
                placeholder="••••••••"
                type="password"
              />

              {errors.password && (
                <p className="text-red-500 ">{errors.password}</p>
              )}
            </div>

            {errors.non_field_errors && (
              <p className="text-red-500 ">{errors.non_field_errors}</p>
            )}

            <Button
              className="inline-flex w-full"
              type="submit"
              disabled={loading}
            >
              Log in {loading && <SpinningSvg />}
            </Button>
            <Link to="/" onClick={handleClickBack}>
              <Button
                variant="outline"
                className="inline-flex w-full mt-2"
                type="submit"
                disabled={loading}
              >
                Back
              </Button>
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
